import {
    Component,
    Input,
    Output,
    ViewChild,
    ElementRef,
    ChangeDetectionStrategy,
    EventEmitter,
    SimpleChanges,
    ChangeDetectorRef,
} from '@angular/core';
import moment from 'moment';
import Tooltip from 'cal-heatmap/plugins/Tooltip';

// CALENDAR HEATMAP
// This is not originally an Angular component.
// Wrapping it here into one.
// Encapsulating it so that it listens to $onChanges and updates the internal parameters based on that.

// cal-heatmap is an ES module whose typings use `export =`. TypeScript 5.8 rejects a default import of such
// typings (TS1192), so the module is imported as a namespace: its types are read from it, and the class is its
// default export at runtime (the bundler's ESM build, Jest's mock). The instance type is the typings' global
// `CalHeatmap` class.
import * as CalHeatmapModule from 'cal-heatmap';
import { ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { utcTimezonePlugin } from './utc-timezone-dayjs-plugin';

// library has incorrect typing for TooltipOptions, let's introduce them here
import type dayjs from 'dayjs';

const CalHeatMap = (CalHeatmapModule as unknown as { default: new () => CalHeatmap }).default;
interface PopperOptions {
    placement: any;
    modifiers: any[];
    strategy: any;
    onFirstUpdate?: any;
}
interface TooltipOptions extends CalHeatmapModule.PluginOptions, PopperOptions {
    enabled: boolean;
    text: (timestamp: CalHeatmapModule.Timestamp, value: number, dayjsDate: dayjs.Dayjs) => string;
}

@Component({
    selector: 'cal-heatmap',
    templateUrl: './cal-heatmap.component.html',
    styleUrls: ['./cal-heatmap.css', './cal-heatmap.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class CalHeatMapComponent {
    /** Inputs that live inside a nested cal-heatmap config object rather than directly on state. */
    private static readonly NESTED_INPUTS = ['data', 'domain', 'timestamp'];

    @ViewChild('heatMap', { static: true }) heatMap: ElementRef;

    state: any;
    cal: CalHeatmap;
    calElement: any;
    textWelding: string;
    textOnline: string;
    loading: boolean;

    @Input() data: { timestamp: string; value: number }[] = [];
    /** Number of days/months/etc. to display */
    @Input() range: number;
    @Input() width: number;
    @Input() height: number;
    @Input() domain: 'year' | 'month' | 'day' | 'week' = 'month';
    @Input() timestamp: Date;

    @Output() dateClicked = new EventEmitter<HeatmapClickEvent>();

    requestUpdate$ = new ReplaySubject<void>();
    private onDestroy$ = new Subject<void>();

    constructor(private cdRef: ChangeDetectorRef) {
        this.state = {};
    }

    ngOnInit() {
        this.loading = true;
    }

    getData(): { t: number; v: number }[] {
        return this.data.map((datapoint) => ({ t: +Math.floor(Date.parse(datapoint.timestamp)), v: datapoint.value }));
    }

    ngAfterViewInit() {
        this.cal = null;
        this.calElement = this.heatMap.nativeElement;
        // Defaults
        this.state = {
            // DATA FOR THE COMPONENT-------
            // initial values from the attributes, with some default strings if none provided
            data: {
                source: this.getData(),
                x: 't',
                y: 'v',
                defaultValue: 0,
            },
            date: {
                // cal-heatmap anchors every domain and cell at midnight UTC, and the data it is
                // filled with is bucketed per UTC day. Pinning the timezone keeps the calendar on
                // that one convention instead of letting it guess the browser's, which shifted
                // every label one domain back for viewers west of UTC. [KC-5927] dayjs' own UTC
                // conversion misplaces a day around some viewers' DST change; see utcTimezonePlugin.
                timezone: 'UTC',
                start: this.timestamp,
                locale: { weekStart: 1 },
                highlight: [new Date()],
            },
            width: this.width,
            height: this.height,
            domain: {
                type: this.domain,
                label: {
                    position: 'top',
                    text: null,
                },
            },
            range: this.range,

            // other defaults
            subDomain: {
                type: 'day',
                height: 20,
                width: 20,
                gutter: 1,
                label: '',
                radius: 2,
            },
            verticalOrientation: false,
        };
        this.validate();
        this.setDomain();

        // Start drawing heatmap when view ready
        this.requestUpdate$.pipe(takeUntil(this.onDestroy$)).subscribe(() => this.initHeatMap());
    }

    validate() {
        // Sanity check of the given attributes
        if (!this.state.data.source) {
            this.state.data.source = [];
        }
        if (!this.state.width) {
            this.state.width = 200;
        }
        if (!this.state.height) {
            this.state.height = 100;
        }
        if (
            !this.state.domain.type ||
            (this.state.domain.type !== 'year' &&
                this.state.domain.type !== 'month' &&
                this.state.domain.type !== 'week' &&
                this.state.domain.type !== 'day')
        ) {
            this.state.domain.type = 'month';
        }
        if (!this.state.date.start) {
            this.state.date.start = new Date();
        }
        if (!this.state.range) {
            this.state.range = 1;
        }
    }

    // Make this a component that updates it's state based on changes to the attributes
    ngOnChanges(changes: SimpleChanges) {
        Object.keys(changes)
            .filter((change) => !CalHeatMapComponent.NESTED_INPUTS.includes(change))
            .forEach((change) => {
                this.state[change] = changes[change].currentValue;
            }, this);

        // make sure that state has been initialized
        if (!this.state.domain) {
            return;
        }

        if (changes.domain) {
            this.state.domain.type = changes.domain.currentValue;
        }
        if (changes.data) {
            this.state.data.source = this.getData();
        }
        // Compare the value, not the reference: consumers hand us a fresh Date on every
        // refresh, and a repaint tears down any open tooltip.
        let startMoved = false;
        if (changes.timestamp) {
            startMoved = +changes.timestamp.currentValue !== +this.state.date.start;
            this.state.date.start = changes.timestamp.currentValue;
        }

        this.validate();

        // A new start date or domain needs the whole calendar repainted; cal-heatmap has no
        // way to move an existing one. A data-only change can be filled in place.
        if (changes.domain || changes.range || startMoved) {
            this.setDomain();
        } else if (changes.data) {
            this.update();
        }
    }

    _resetCal() {
        this.requestUpdate$.next();
    }
    setDomain() {
        this._setDomainAttributes();
        this._resetCal();
    }
    _setDomainAttributes() {
        const w = this.state.width;
        const h = this.state.height;

        switch (this.state.domain.type) {
            case 'day': {
                this.state.subDomain.height = Math.min(h / 1, (w - 30) / 24);
                this.state.subDomain.width = Math.min(h / 1, (w - 30) / 24);
                this.state.subDomain.gutter = 2;
                this.state.subDomain.type = 'hour';
                this.state.range = 1;
                this.state.verticalOrientation = true;
                this.state.domain.label = {
                    text:
                        this.state.subDomain.height < 10
                            ? function (timestamp) {
                                  return moment.utc(timestamp).format('ddd');
                              }
                            : function (timestamp) {
                                  return moment.utc(timestamp).format('ddd DD.MM.');
                              },
                    position: 'left',
                    width: this.state.cellSize < 10 ? 33 : 66,
                    offset: { x: 3, y: this.state.cellSize < 10 ? 6 : 12 },
                };
                this.state.subDomain.label = this.state.subDomain.height < 10 ? '' : 'HH';
                break;
            }
            case 'week': {
                this.state.domain.type = 'day';
                this.state.subDomain.height = Math.min((h - 6) / 1, (w - 30) / 24);
                this.state.subDomain.width = Math.min((h - 6) / 1, (w - 30) / 24);
                this.state.subDomain.gutter = 2;
                this.state.subDomain.type = 'hour';
                this.state.range = 7;
                this.state.verticalOrientation = true;
                this.state.domain.label = {
                    text:
                        this.state.cellSize < 10
                            ? function (timestamp) {
                                  return moment.utc(timestamp).format('ddd');
                              }
                            : function (timestamp) {
                                  return moment.utc(timestamp).format('ddd DD.MM.');
                              },
                    position: 'left',
                    width: this.state.cellSize < 10 ? 33 : 66,
                    offset: { x: 3, y: this.state.subDomain.height < 10 ? 6 : 12 },
                };
                this.state.subDomain.label = this.state.subDomain.height < 10 ? '' : 'HH';
                break;
            }
            case 'month': {
                this.state.subDomain.height = Math.min(h / 6, w / 7);
                this.state.subDomain.width = Math.min(h / 6, w / 7);
                this.state.subDomain.gutter = 1;
                this.state.subDomain.type = 'xDay';
                this.state.verticalOrientation = false;
                this.state.domain.label = {
                    text: function (timestamp) {
                        return moment.utc(timestamp).format('MMM');
                    },
                    position: 'top',
                };
                this.state.subDomain.label = this.state.subDomain.height < 15 ? '' : 'DD';
                break;
            }
            case 'year': {
                this.state.subDomain.height = Math.min(h / 7, w / 53);
                this.state.subDomain.width = Math.min(h / 7, w / 53);
                this.state.subDomain.gutter = 1;
                this.state.subDomain.type = 'xDay';
                this.state.range = 1;
                this.state.verticalOrientation = false;
                this.state.domain.label = { text: null, position: 'top' };
                this.state.subDomain.label = null;
                break;
            }
        }
    }

    update() {
        if (this.cal) {
            this.cal.fill(this.state.data.source);
        }
    }

    initHeatMap() {
        if (this.cal) {
            this.cal.destroy();
        }

        this.cal = new CalHeatMap();
        this.cal.extendDayjs(utcTimezonePlugin);

        // Configure listeners
        this.cal.on('click', (event?, timestamp?, value?) => {
            this.dateClicked.emit({ date: new Date(timestamp), value }); // let consumer decide utc or local
        });

        let dataMaxVal = Math.max(...this.state.data.source.map((item) => item.v));
        // set max to 1 if no data or all data is 0
        if (!isFinite(dataMaxVal) || dataMaxVal === 0) {
            dataMaxVal = 1;
        }

        this.cal.paint(
            {
                date: this.state.date,
                data: this.state.data,
                itemSelector: this.calElement,
                range: this.state.range,
                domain: this.state.domain,
                verticalOrientation: this.state.verticalOrientation,
                subDomain: this.state.subDomain,
                scale: {
                    color: {
                        range: [
                            'rgb(178, 178, 178)', // #b2b2b2
                            'rgb(124, 207, 237)', // #7ccfed
                            'rgb(56, 202, 255)', // #38caff
                            'rgb(8, 175, 246)', // #08aff6
                            'rgb(0, 157, 222)', // #009dde
                            'rgb(0, 121, 173)', // #0079ad
                            'rgb(0, 106, 150)', // #006a96
                            'rgb(0, 80, 112)', // #005070
                            'rgb(0, 66, 92)', // #00425c
                            'rgb(0, 54, 75)', // #00364b
                        ],
                        interpolate: 'rgb',
                        type: 'threshold', // follows domain thresholds
                        domain: [0.1, ...Array.from({ length: 10 }, (_, i) => (i * dataMaxVal) / 10).slice(1)], // starts with 0.1 to use first color of range
                    },
                },
                theme: 'light',
            },
            [
                [
                    Tooltip,
                    {
                        enabled: true,
                        text: function (_date: number, value: number, dayjsDate: any) {
                            /**
                             *
                             * Helper method to convert data count (seconds) to more readable form
                             */
                            const _numberFormatToString = (count: number) => {
                                return `${(count || 0).toFixed(1)}`;
                            };

                            return `${_numberFormatToString(value)} kWh ${dayjsDate.format('DD.MM.YYYY')}`;
                        },
                        placement: 'top',
                        modifiers: [
                            // use offset to resolve flickering bug, tooltip is too close to mouseover
                            {
                                name: 'offset',
                                options: {
                                    offset: [0, 16],
                                },
                            },
                        ],
                    } as TooltipOptions,
                ],
            ]
        );
        this.loading = false;
        this.cdRef.markForCheck();
    }
}

export interface HeatmapClickEvent {
    date: Date;
    value: any;
}
